/**
 * secret-masking.js — the client side of cluster-api's server-side credential
 * masking on the two `connection-info` endpoints:
 *
 *   GET /cluster/dev-environments/<id>/connection-info          (2.25.43)
 *   GET /cluster/app-templates/installed/connection-info        (2.25.45)
 *
 * The server decides what a caller may see. The payload arrives with credential
 * values already replaced by a stub (`••••••••`), or — for URIs — with only the
 * password segment stubbed, and carries a `secret_masking` block describing what
 * it did:
 *
 *   secret_masking: {
 *     masked: true,                       // values in this payload are stubs
 *     masked_keys: ['credentials.password', 'mongodb.connection_string'],
 *     masked_count: 2,
 *     revealed: false,                    // true only on a granted ?reveal=1
 *     reveal_available: false,            // may THIS caller ask for clear values
 *     stub: '••••••••'
 *   }
 *
 * Each masked section also carries its own `masked: true` + `masked_keys: [...]`.
 * Those two keys are bookkeeping, not connection details — `SECTION_MASK_KEYS`
 * exists so renderers drop them instead of printing "Masked: true" at the
 * customer.
 *
 * The rule this module encodes: NEVER mask locally and never unmask locally.
 * Render what the API returned. A reveal is a fresh request with `?reveal=1`,
 * which the server answers only for a caller who passes an instance-scoped
 * `secret:viewer` check (and audits). For a customer looking at their own app
 * that check is False, so `reveal_available` is false and no reveal control is
 * offered at all — an affordance that always fails is worse than none.
 *
 * There is one exception to "never unmask locally", and it is not a render: a
 * **copy**. Putting the stub on the clipboard is worse than copying nothing —
 * it pastes into a login form or a config as a plausible string and fails with
 * nothing to say it was never the password. So Copy on a hidden row asks the
 * server for that one value (`?reveal=1&keys=<path>`) and hands it straight to
 * the clipboard without ever painting it, because the person clicking Copy may
 * be sharing their screen. The decisions that needs — which rows are hidden,
 * whether this caller may ask, what the button says when they may not, and
 * what a copied block looks like when a value is withheld — are the pure
 * functions in the second half of this file.
 *
 * Pure: no imports, no DOM, no `@/` aliases. Covered by
 * `node --test tests/unit/secret-masking.test.mjs` and
 * `tests/unit/conn-copy-reveal.test.mjs`.
 *
 * Author: Matew Makhatadze
 */

/** Per-section bookkeeping keys that must never be rendered as a value row. */
export const SECTION_MASK_KEYS = Object.freeze(['masked', 'masked_keys'])

/** Top-level payload key holding the masking report. */
export const MASKING_KEY = 'secret_masking'

const EMPTY = Object.freeze({
  present: false,
  masked: false,
  revealed: false,
  revealAvailable: false,
  maskedKeys: Object.freeze([]),
  maskedCount: 0,
  stub: ''
})

/** `null` or `undefined` — the lint config forbids the `!= null` shorthand. */
function isNil(v) {
  return v === null || v === undefined
}

function asObject(v) {
  return v && typeof v === 'object' && !Array.isArray(v) ? v : null
}

/**
 * Normalise `payload.secret_masking` into a stable view-model. Tolerant of an
 * older server that sends nothing: then `present` is false, `masked` is false
 * and `revealAvailable` is false — i.e. render the payload as-is and offer no
 * reveal, which is exactly the safe posture.
 *
 * @param {object|null} payload the connection-info payload (verbatim `data`)
 * @returns {{present:boolean, masked:boolean, revealed:boolean,
 *            revealAvailable:boolean, maskedKeys:string[], maskedCount:number,
 *            stub:string}}
 */
export function connectionInfoMasking(payload) {
  const p = asObject(payload)
  const m = p ? asObject(p[MASKING_KEY]) : null
  if (!m) return EMPTY
  const maskedKeys = Array.isArray(m.masked_keys)
    ? m.masked_keys.filter((k) => k != null && k !== '').map(String)
    : []
  const rawCount = Number(m.masked_count)
  const maskedCount = Number.isFinite(rawCount) && rawCount >= 0 ? Math.trunc(rawCount) : maskedKeys.length
  return {
    present: true,
    masked: m.masked === true,
    revealed: m.revealed === true,
    revealAvailable: m.reveal_available === true,
    maskedKeys,
    maskedCount,
    stub: typeof m.stub === 'string' ? m.stub : ''
  }
}

/**
 * The single calm line the modal shows about hidden values — or null when
 * there is nothing to say (no masking block, or nothing was hidden).
 *
 * Written from the customer's side of the screen: the values are hidden, and
 * we do not claim to know where the real one lives, because the payload does
 * not say.
 *
 * @param {ReturnType<typeof connectionInfoMasking>} state
 * @returns {{tone:'info'|'warning', text:string}|null}
 */
export function maskingNotice(state) {
  const s = state && typeof state === 'object' ? state : EMPTY
  if (s.revealed) {
    return {
      tone: 'warning',
      text: s.maskedCount
        ? `Showing ${s.maskedCount} credential value${s.maskedCount === 1 ? '' : 's'} in full. Each reveal is recorded.`
        : 'Showing credential values in full. Each reveal is recorded.'
    }
  }
  if (!s.masked) return null
  return {
    tone: 'info',
    text: s.maskedCount
      ? `${s.maskedCount} credential value${s.maskedCount === 1 ? '' : 's'} ${s.maskedCount === 1 ? 'is' : 'are'} hidden.`
      : 'Credential values are hidden.'
  }
}

/** Short label for a per-section badge, or '' when the section hid nothing. */
export function sectionMaskedLabel(section) {
  const s = asObject(section)
  if (!s) return ''
  const n = Array.isArray(s.masked_keys) ? s.masked_keys.length : 0
  if (s.masked !== true && !n) return ''
  return n ? `${n} hidden` : 'hidden'
}

/**
 * Was this failure the server refusing a `?reveal=1` because the caller does
 * not hold `secret:viewer` on the instance? cluster-api answers 403 with
 * `error_kind: 'secret_reveal_forbidden'`; the envelope has moved around
 * between services, so look for the kind in every place it has ever sat.
 */
export function isSecretRevealForbidden(err) {
  const resp = err && err.response
  if (!resp || resp.status !== 403) return false
  const body = asObject(resp.data) || {}
  const candidates = []
  const push = (v) => { if (typeof v === 'string' && v) candidates.push(v) }
  push(body.error_kind)
  push(body.error)
  push(body.code)
  const errors = body.errors
  if (Array.isArray(errors)) {
    errors.forEach((e) => {
      const o = asObject(e)
      if (o) { push(o.error_kind); push(o.error); push(o.code) }
    })
  } else {
    const o = asObject(errors)
    if (o) { push(o.error_kind); push(o.error); push(o.code) }
  }
  const details = asObject(body.data) || asObject(body.details)
  if (details) { push(details.error_kind); push(details.error) }
  return candidates.includes('secret_reveal_forbidden')
}

/**
 * Copy for a failed reveal. The masked payload stays on screen underneath —
 * the caller must not drop it.
 *
 * @param {Error} err
 * @returns {{title:string, message:string, tone:'warning'|'error'}}
 */
export function revealErrorCopy(err) {
  if (isSecretRevealForbidden(err)) {
    return {
      title: 'Values stay hidden',
      message: 'Showing these credentials needs a secret:viewer grant on this app. Ask your platform operator for it.',
      tone: 'warning'
    }
  }
  const status = err && err.response ? err.response.status : null
  if (status === 401) {
    return { title: 'Session expired', message: 'Sign in again, then try showing the values.', tone: 'warning' }
  }
  return {
    title: 'Could not show the values',
    message: 'The platform could not read the credentials just now. The hidden values below are still current.',
    tone: 'error'
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Copying a hidden value
//
// Everything below answers one question in a pure function so the modal only
// has to do the fetching, the clipboard and the timers.
// ───────────────────────────────────────────────────────────────────────────

/**
 * Key names treated as credential-bearing when the server hid something but
 * named nothing. Last resort only: the server classified the VALUE, so its
 * inventory catches a token parked under `note` that no name heuristic would.
 */
export const SECRET_KEY_HINT =
  /(password|secret|token|key|connection_string|connection_strings|cookie)/i

/** Why Copy is off: the caller holds no `secret:viewer` on this instance. */
export const COPY_FORBIDDEN_NOTE =
  'Copying this value needs a secret:viewer grant on this app. Ask your platform operator for it.'

/** Why Copy is off: we do not know which app to ask about. */
export const COPY_NO_TARGET_NOTE =
  'The platform cannot look this value up from here. Reopen the app page and try again.'

/** Title on a Copy that will fetch the real value first. */
export const COPY_RECORDED_TITLE = 'Copy the real value — the reveal is recorded'

/** The clipboard itself refused (an insecure context, or browser policy). */
export const CLIPBOARD_BLOCKED_NOTE =
  'Your browser would not let the page write to the clipboard, so nothing was copied.'

/**
 * Every dotted path the server says it hid, as one flat list.
 *
 * Two inventories arrive and both are read: the top-level report lists dotted
 * paths (`mongodb.password`), while each touched section lists its own fields
 * bare (`password` under `mongodb`). Normalising both to dotted paths means a
 * single set answers for every row, and a server that sends only one of the
 * two is still understood.
 *
 * @param {object|null} payload the connection-info payload (verbatim `data`)
 * @returns {string[]} dotted paths, de-duplicated, order of first appearance
 */
export function connectionMaskedKeys(payload) {
  const p = asObject(payload)
  if (!p) return []
  const out = []
  const add = (k) => {
    if (isNil(k) || k === '') return
    const s = String(k)
    if (!out.includes(s)) out.push(s)
  }
  const top = asObject(p[MASKING_KEY])
  if (top && Array.isArray(top.masked_keys)) top.masked_keys.forEach(add)
  Object.keys(p).forEach((section) => {
    if (section === MASKING_KEY) return
    const s = asObject(p[section])
    if (!s || !Array.isArray(s.masked_keys)) return
    s.masked_keys.forEach((k) => {
      if (isNil(k) || k === '') return
      const key = String(k)
      add(key.includes('.') ? key : `${section}.${key}`)
    })
  })
  return out
}

/**
 * Read a dotted / indexed path (`mongodb.pod_vips[0].connection_string`) out
 * of a reveal response. The paths are the server's own, so this is the inverse
 * of how it built them. Anything that is not a scalar reads as `undefined` —
 * a row shows one value, and half an object is not one.
 */
export function valueAtPath(payload, path) {
  if (!payload || typeof payload !== 'object' || !path) return undefined
  const steps = String(path)
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter((s) => s !== '')
  let node = payload
  for (const step of steps) {
    if (isNil(node) || typeof node !== 'object') return undefined
    node = node[step]
  }
  return typeof node === 'string' || typeof node === 'number' ? String(node) : undefined
}

/**
 * Pull the clear values for `paths` out of a reveal response. A path the
 * response did not carry is simply absent — the caller must treat that as
 * "withheld", never as an empty credential.
 */
export function collectRevealed(payload, paths) {
  const out = {}
  ;(paths || []).forEach((p) => {
    const v = valueAtPath(payload, p)
    if (v !== undefined) out[p] = v
  })
  return out
}

/**
 * Is this value on screen the server's stub rather than data? True for the
 * plain stub and for a URI with only its password segment replaced, which is
 * why containment is checked and not just equality.
 *
 * It is NOT a complete test, and must stay a fallback behind the inventory.
 * Measured against the deployed cluster-api on 2026-10-06 (`mongodb-sb`), a
 * payload declaring `stub: '••••••••'` returned its password as `abc…xy` —
 * the truncating form — so the declared stub matched nothing in the value it
 * supposedly replaced. `masked_keys` is the authority; this only catches a
 * server that masked without naming what it masked.
 */
export function valueIsStubbed(value, stub) {
  if (!stub || typeof stub !== 'string') return false
  const v = isNil(value) ? '' : String(value)
  return v === stub || v.includes(stub)
}

/**
 * Is the value rendered for `field` a stub, i.e. must Copy fetch the real one?
 *
 * Three signals, in the order they deserve trust:
 *  1. the server's own inventory of hidden paths;
 *  2. the value matching the stub the server said it used (a server that
 *     masked without listing, or a path spelled differently than the row key);
 *  3. the key-name heuristic, used only when the server hid something and
 *     named nothing at all.
 *
 * A payload that hid nothing — an older cluster-api, or one already revealed
 * in full — returns false everywhere, so Copy keeps working verbatim.
 *
 * @param {ReturnType<typeof connectionInfoMasking>} state
 * @param {string[]} maskedKeys from `connectionMaskedKeys(payload)`
 * @param {{key?:string, value?:*}} field a rendered row
 */
export function isHiddenValue(state, maskedKeys, field) {
  const s = state && typeof state === 'object' ? state : EMPTY
  if (!s.masked || s.revealed) return false
  const f = asObject(field) || {}
  const path = isNil(f.key) ? '' : String(f.key)
  const keys = Array.isArray(maskedKeys) ? maskedKeys : []
  if (keys.includes(path)) return true
  if (valueIsStubbed(f.value, s.stub)) return true
  if (keys.length) return false
  const leaf = path.includes('.') ? path.slice(path.lastIndexOf('.') + 1) : path
  return SECRET_KEY_HINT.test(leaf)
}

/**
 * What the Copy button on one row does, and what the person reads when it
 * cannot do anything. Never returns a state that copies a stub.
 *
 * `reason` is rendered as text beside the row, not only as a `title`: a
 * disabled control whose reason you cannot read is the same dead end as one
 * that silently does the wrong thing.
 *
 * @param {object} o
 * @param {boolean} o.hidden the value on screen is a stub
 * @param {boolean} o.canAsk the server said this caller may reveal, AND we
 *   know which app to ask about
 * @param {boolean} o.busy a reveal for this row is in flight
 * @param {string} o.blocked why we cannot ask, when `canAsk` is false
 * @returns {{disabled:boolean, needsReveal:boolean, title:string, reason:string}}
 */
export function copyAvailability({ hidden = false, canAsk = false, busy = false, blocked = '' } = {}) {
  if (!hidden) {
    return { disabled: false, needsReveal: false, title: busy ? 'Working…' : 'Copy', reason: '' }
  }
  if (busy) {
    return { disabled: true, needsReveal: true, title: 'Fetching the value…', reason: '' }
  }
  if (!canAsk) {
    const reason = blocked || COPY_FORBIDDEN_NOTE
    return { disabled: true, needsReveal: true, title: reason, reason }
  }
  return { disabled: false, needsReveal: true, title: COPY_RECORDED_TITLE, reason: '' }
}

/** One line naming the credentials a copied block does not contain. */
export function withheldNote(labels) {
  const names = (labels || []).filter((l) => !isNil(l) && l !== '').map(String)
  if (!names.length) return ''
  const one = names.length === 1
  const plural = one ? '' : 's'
  const read = one ? 'it' : 'them'
  return `${names.length} credential value${plural} withheld (${names.join(', ')}) — ` +
    `you do not have permission to read ${read}.`
}

/**
 * The "Copy all" block.
 *
 * `clear` maps a hidden row's dotted path to its revealed value. A hidden row
 * with no entry there is **omitted**, and its label is collected into
 * `withheld` for a single trailing `#` comment. The old behaviour — printing
 * the stub — put something that reads exactly like a credential into whatever
 * the block was pasted into; printing a placeholder where the value goes does
 * the same to a config parser. A gap plus one honest comment cannot be
 * mistaken for a password.
 *
 * @returns {{text:string, withheld:string[]}}
 */
export function buildCopyBlock({
  ingressUrl = '',
  hostnames = [],
  ingressRows = [],
  internalUrl = '',
  dnsRows = [],
  components = [],
  clear = {},
  hidden = () => false
} = {}) {
  const lines = []
  const withheld = []
  if (ingressUrl) lines.push(`URL: ${ingressUrl}`)
  ;(hostnames || []).forEach((h) => lines.push(`Hostname: ${h}`))
  ;(ingressRows || []).forEach((r) => lines.push(`${r.name}: ${r.url}`))
  if (internalUrl) lines.push(`Internal URL: ${internalUrl}`)
  ;(dnsRows || []).forEach((r) => lines.push(`${r.host} -> ${r.ip}`))
  ;(components || []).forEach((c) => {
    ;(c.fields || []).forEach((f) => {
      if (!hidden(f)) {
        lines.push(`${f.label}: ${f.value}`)
        return
      }
      const value = Object.prototype.hasOwnProperty.call(clear, f.key) ? clear[f.key] : undefined
      if (isNil(value) || value === '') {
        withheld.push(f.label)
        return
      }
      lines.push(`${f.label}: ${value}`)
    })
  })
  if (withheld.length) lines.push(`# ${withheldNote(withheld)}`)
  return { text: lines.join('\n'), withheld }
}

/**
 * One sentence for a copy-reveal that failed, written for the row it sits in.
 *
 * `revealErrorCopy` is the two-part banner for the whole-payload "Show values"
 * control and says "Showing these credentials…" — the wrong verb for someone
 * who only wanted to paste. A 403 here is the answer, not a fault, and reads
 * as the same grant sentence the disabled button carries, so the row says the
 * same thing whether permission was missing before the click or discovered
 * during it.
 */
export function copyErrorNote(err) {
  if (isSecretRevealForbidden(err)) return COPY_FORBIDDEN_NOTE
  const status = err && err.response ? err.response.status : null
  if (status === 401) return 'Your session has expired. Sign in again, then copy.'
  if (err && err.valueWithheld === true) {
    return 'The platform did not return this value. It may have changed since this page loaded — refresh and try again.'
  }
  return 'The platform could not read this value just now. Nothing was copied.'
}
